import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DiscordRewardInfo } from '@/components/discord/DiscordRewardInfo';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { discordService, type CompletedDiscordLink } from '@/lib/services/discord.service';
import { Caixa } from '@/construtor/caixa';

type Estado =
  | { tipo: 'trabalhando' }
  | { tipo: 'sucesso'; vinculo: CompletedDiscordLink }
  | { tipo: 'cancelado' }
  | { tipo: 'login' }
  | { tipo: 'erro'; mensagem: string };

/**
 * A volta do Discord depois do OAuth do vínculo.
 *
 * Location: src/pages
 * Why: lê `code` e `state` da URL, **tira os dois da barra de endereço na
 * hora** (para não ficarem no histórico nem irem num link copiado) e manda
 * para a API com a sessão do jogador. Quem decide se o `state` é desta sessão
 * é a API; aqui não há regra nenhuma.
 *
 * Deu certo: um modal confirma o vínculo e mostra a recompensa, quando a loja
 * dá uma. O jogador sai dele para o perfil, onde o vínculo fica à vista.
 */
export default function DiscordCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useStore();
  const { isAuthenticated, isLoading, openLoginModal } = useAuth();

  // Lidos uma vez, antes de limpar a URL.
  const [parametros] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    window.history.replaceState(null, '', window.location.pathname);
    return { code: p.get('code'), state: p.get('state'), erro: p.get('error') };
  });
  const [estado, setEstado] = useState<Estado>({ tipo: 'trabalhando' });
  const enviado = useRef(false);

  useEffect(() => {
    if (parametros.erro || !parametros.code || !parametros.state) {
      setEstado({ tipo: 'cancelado' });
      return;
    }
    if (isLoading || !store?.id) return;
    if (!isAuthenticated) {
      setEstado({ tipo: 'login' });
      openLoginModal();
      return;
    }
    // O code vale uma vez: nada de mandar de novo num segundo render.
    if (enviado.current) return;
    enviado.current = true;
    setEstado({ tipo: 'trabalhando' });

    discordService
      .complete(store.id, parametros.code, parametros.state)
      .then((vinculo) => setEstado({ tipo: 'sucesso', vinculo }))
      .catch((err: any) => {
        const mensagem = err?.response?.data?.message;
        setEstado({ tipo: 'erro', mensagem: typeof mensagem === 'string' ? mensagem : t('store.discord.error') });
      });
  }, [parametros, isLoading, isAuthenticated, store?.id, openLoginModal, t]);

  const irParaPerfil = () => navigate('/perfil?tab=discord', { replace: true });

  return (
    <div className="container mx-auto flex min-h-[60vh] items-center justify-center px-4 py-16">
      <Caixa id="perfil.discord.retorno" miolo>
      <Card className="glass-strong w-full max-w-md border-border/50">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center" data-testid="discord-callback">
          <h1 className="text-xl font-bold">{t('store.discord.callback_title')}</h1>

          {estado.tipo === 'trabalhando' && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('store.discord.callback_working')}
            </p>
          )}
          {estado.tipo === 'sucesso' && (
            <p className="flex items-center gap-2 text-emerald-500">
              <CheckCircle2 className="h-5 w-5" />
              {t('store.discord.callback_success', { username: estado.vinculo.username })}
            </p>
          )}
          {estado.tipo === 'cancelado' && <p className="text-muted-foreground">{t('store.discord.callback_cancelled')}</p>}
          {estado.tipo === 'login' && <p className="text-muted-foreground">{t('store.discord.callback_login')}</p>}
          {estado.tipo === 'erro' && (
            <p className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5 shrink-0" />
              {estado.mensagem}
            </p>
          )}

          {estado.tipo !== 'trabalhando' && (
            <Button asChild variant="outline">
              <Link to="/perfil?tab=discord">{t('store.discord.callback_back')}</Link>
            </Button>
          )}
        </CardContent>
      </Card>
      </Caixa>

      {estado.tipo === 'sucesso' && (
        <Dialog open onOpenChange={(aberto) => !aberto && irParaPerfil()}>
          <DialogContent className="max-w-md" data-testid="discord-linked-modal">
            <DialogHeader className="items-center text-center sm:text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <DialogTitle className="text-2xl">{t('store.discord.modal_title')}</DialogTitle>
              <DialogDescription>
                {t('store.discord.modal_description', { username: estado.vinculo.username, store: store?.name ?? '' })}
              </DialogDescription>
            </DialogHeader>

            {estado.vinculo.reward && <DiscordRewardInfo reward={estado.vinculo.reward} />}
            {estado.vinculo.rewardSkipped === 'account_too_new' && (
              <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">{t('store.discord.reward_too_new')}</p>
            )}

            <DialogFooter className="sm:justify-center">
              <Button onClick={irParaPerfil} className="w-full sm:w-auto">
                {t('store.discord.modal_button')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
